/**
 * Maneuver vocabulary, using GraphHopper's `sign` numbering.
 *
 * The same numbers Android and iOS use, because the numbers are what is stored — a sign
 * written by one app is read by the other two. They are kept even where nothing calls
 * GraphHopper any more: a compact, documented encoding that OSM junction data will speak too.
 */
export const ManeuverSign = {
  U_TURN_LEFT: -8,
  KEEP_LEFT: -7,
  TURN_SHARP_LEFT: -3,
  TURN_LEFT: -2,
  TURN_SLIGHT_LEFT: -1,
  CONTINUE: 0,
  TURN_SLIGHT_RIGHT: 1,
  TURN_RIGHT: 2,
  TURN_SHARP_RIGHT: 3,
  FINISH: 4,
  REACHED_VIA: 5,
  USE_ROUNDABOUT: 6,
  KEEP_RIGHT: 7,
  U_TURN_RIGHT: 8
} as const;

/** Signs that end the route. */
export function isTerminal(sign: number): boolean {
  return sign === ManeuverSign.FINISH || sign === ManeuverSign.REACHED_VIA;
}

/**
 * Turn angle to a sign, so icons and wording share one vocabulary.
 *
 * The thresholds match `ManeuverSign.forAngle` on both phones exactly. They decide what a
 * corner is *called*, so a route read on the web and walked on a phone has to use the same
 * ones or the two disagree about the same bend.
 */
export function signForAngle(degrees: number): number {
  const magnitude = Math.abs(degrees);
  const right = degrees >= 0;
  if (magnitude < 35) return ManeuverSign.CONTINUE;
  if (magnitude < 60) return right ? ManeuverSign.TURN_SLIGHT_RIGHT : ManeuverSign.TURN_SLIGHT_LEFT;
  if (magnitude < 115) return right ? ManeuverSign.TURN_RIGHT : ManeuverSign.TURN_LEFT;
  if (magnitude < 155) return right ? ManeuverSign.TURN_SHARP_RIGHT : ManeuverSign.TURN_SHARP_LEFT;
  return right ? ManeuverSign.U_TURN_RIGHT : ManeuverSign.U_TURN_LEFT;
}
